import {
  completeQueryResult,
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';
import { Observable, filter, map, of, pipe, pluck, take, tap } from 'rxjs';

import { Injectable } from '@angular/core';

import {
  AutoLoginGQL,
  FinalizePhotographerRegistrationGQL,
  CheckAuthCodeGQL,
  CheckLoginGQL,
  CheckPasswordGQL,
  CheckPasswordResetCodeGQL,
  LogoutCurrentUserGQL,
  StartPhotographerRegistrationGQL,
  type PasswordCheckResponse,
  RegisterPhotographerInitErrorCode,
  ResetPasswordGQL,
  SendPasswordResetEmailGQL,
  TwoFactorMethod,
  UserType,
} from './graphql';

import { JWTService } from './jwt-parser-service';
import { DeviceService } from './device.service';
import { RxjsOperators } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import { decodeJwtPayload as jwt_decode } from 'ZalamoLibrary/src/lib/core/helpers/decode-jwt';

export interface LoginCheckResponse {
  status: boolean;
  availableAccounts?: Array<string>;
}
export interface PasswordResetResult extends PasswordCheckResponse {
  userType: UserType.RetouchManager | UserType.Retoucher | null;
}

export interface PasswordLoginResult {
  status: boolean;
  has2fa?: boolean;
  firstName: string;
  twoFactorMethod: TwoFactorMethod | null;
}

interface RetouchsyncAuthTokenPayload {
  type?: unknown;
}

export const passwordResetUserTypeFromToken = (
  token: string | null | undefined,
): PasswordResetResult['userType'] => {
  if (!token) {
    return null;
  }
  try {
    const { type } = jwt_decode<RetouchsyncAuthTokenPayload>(token);
    if (type === UserType.RetouchManager || type === UserType.Retoucher) {
      return type;
    }
  } catch (_) {
    // A malformed token must not change the password-reset flow.
  }
  return null;
};

@Injectable({
  providedIn: 'root',
})
export class ZalamoAuthService {
  private lastLogin = '';
  private lastPasswordResetCode = '';
  private lastPhoneForRegistration = '';
  private twoFactorChallengeId: string | null = null;

  get token() {
    return this.jwt.getToken();
  }

  constructor(
    private loginExists: CheckLoginGQL,
    private checkPasswordQuery: CheckPasswordGQL,
    private jwt: JWTService,
    private device: DeviceService,
    private autoLoginGQL: AutoLoginGQL,
    private checkAuthCodeGQL: CheckAuthCodeGQL,
    private sendPasswordResetEmailGQL: SendPasswordResetEmailGQL,
    private checkPasswordResetCodeGQL: CheckPasswordResetCodeGQL,
    private resetPasswordGQL: ResetPasswordGQL,
    private logoutCurrentUserGQL: LogoutCurrentUserGQL,
    private startPhotographerRegistrationGQL: StartPhotographerRegistrationGQL,
    private finalizePhotographerRegistrationGQL: FinalizePhotographerRegistrationGQL,
  ) {}

  checkLogin(login: string): Observable<LoginCheckResponse> {
    /**
     * @todo
     */
    this.lastLogin = login;
    this.twoFactorChallengeId = null;
    return this.loginExists
      .watch({ ...{ fetchPolicy: 'no-cache' }, variables: { login } })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        take(1),
        map((result) => result.data['loginExists']),
      );
  }

  logoutCurrentUser(): Observable<boolean> {
    this.twoFactorChallengeId = null;
    return this.logoutCurrentUserGQL
      .mutate({})
      .pipe(map(({ data }) => data.logoutCurrentUser.status));
  }
  checkZalamoPassword(password: string): Observable<PasswordLoginResult> {
    /**
     * @todo
     */

    this.twoFactorChallengeId = null;

    return this.checkPasswordQuery
      .watch({
        ...{ fetchPolicy: 'no-cache' },
        variables: {
          login: this.lastLogin,
          password,
          deviceId: this.device.deviceId,
          deviceName: navigator.userAgent,
        },
      })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        take(1),
        map((result) => result.data['checkPassword']),
        map(
          ({
            status,
            has2fa,
            token,
            firstName,
            twoFactorMethod,
            twoFactorChallengeId,
          }) => {
            if (status && !has2fa) {
              this.jwt.setToken(token);
            }
            this.twoFactorChallengeId =
              status && has2fa
                ? this.normalizeChallengeId(twoFactorChallengeId)
                : null;
            return {
              status,
              has2fa,
              firstName: String(firstName || ''),
              twoFactorMethod: twoFactorMethod || null,
            };
          },
        ),
      );
  }

  checkCode(
    code: string,
    rememberMe: boolean,
  ): Observable<{ status: boolean }> {
    /**
     * @todo
     */
    return this.checkAuthCodeGQL
      .watch({
        ...{ fetchPolicy: 'no-cache' },
        variables: {
          code,
          rememberMe,
          deviceId: this.device.deviceId,
          twoFactorChallengeId: this.twoFactorChallengeId,
        },
      })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        take(1),
        map((result) => result.data['checkAuthCode']),
        map((codeDetails) => {
          if (codeDetails.status) {
            this.jwt.setToken(codeDetails.token);
            this.twoFactorChallengeId = null;
          }
          return codeDetails;
        }),
      );
  }

  initPasswordRetrieve(login?: string): Observable<{
    status: boolean;
    type: 'sms' | 'email';
  }> {
    /**
     * @todo
     */
    this.twoFactorChallengeId = null;
    const normalizedLogin = String(login || '')
      .trim()
      .toLowerCase();
    if (normalizedLogin) {
      this.lastLogin = normalizedLogin;
    }
    return this.sendPasswordResetEmailGQL
      .mutate({
        ...{
          fetchPolicy: 'no-cache',
        },
        variables: { login: this.lastLogin },
      })
      .pipe(
        map(
          ({
            data: {
              sendPasswordResetEmail: { status },
            },
          }) => ({ status, type: 'email' }),
        ),
      );
  }

  checkPassRetrieveCode(code): Observable<boolean> {
    /**
     * @todo
     */
    this.lastPasswordResetCode = code;
    return this.checkPasswordResetCodeGQL
      .watch({
        ...{
          fetchPolicy: 'no-cache',
        },
        variables: { code, login: this.lastLogin },
      })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        take(1),
        map((apolloQueryResult) => {
          const { data } = completeQueryResult(apolloQueryResult);
          return data.checkPasswordResetCode.status;
        }),
      );
  }

  setNewPassword(
    password: string,
    persistSession = true,
  ): Observable<PasswordResetResult> {
    this.twoFactorChallengeId = null;
    return this.resetPasswordGQL
      .mutate({
        ...{
          fetchPolicy: 'no-cache',
        },
        variables: {
          code: this.lastPasswordResetCode,
          login: this.lastLogin,
          password,
          deviceId: this.device.deviceId,
          deviceName: navigator.userAgent,
        },
      })
      .pipe(
        map(
          ({
            data: {
              resetPassword: {
                status,
                has2fa,
                token,
                firstName,
                twoFactorMethod,
                twoFactorChallengeId,
              },
            },
          }) => {
            if (persistSession && status && !has2fa && token) {
              this.jwt.setToken(token);
            }
            this.twoFactorChallengeId =
              persistSession && status && has2fa
                ? this.normalizeChallengeId(twoFactorChallengeId)
                : null;
            return {
              status,
              has2fa,
              firstName,
              token,
              twoFactorMethod,
              twoFactorChallengeId,
              userType: passwordResetUserTypeFromToken(token),
            };
          },
        ),
      );
  }

  resend2faSms() {
    return of(null);
  }

  startPhotographerRegistration(input: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    password: string;
  }): Observable<{
    status: boolean;
    smsSent: boolean;
    errorCode: RegisterPhotographerInitErrorCode | null;
  }> {
    this.lastPhoneForRegistration = input.phone;
    return this.startPhotographerRegistrationGQL
      .mutate({
        ...{
          fetchPolicy: 'no-cache',
        },
        variables: { input },
      })
      .pipe(pluck('data', 'startPhotographerRegistration'));
  }

  finalizePhotographerRegistration(code: string): Observable<{
    status: boolean;
    firstName: string | null;
  }> {
    return this.finalizePhotographerRegistrationGQL
      .mutate({
        ...{
          fetchPolicy: 'no-cache',
        },
        variables: {
          phone: this.lastPhoneForRegistration,
          code,
          deviceId: this.device.deviceId,
          deviceName: navigator.userAgent,
        },
      })
      .pipe(
        pluck('data', 'finalizePhotographerRegistration'),
        tap(({ status, token }) => {
          if (status && token) {
            this.jwt.setToken(token);
          }
        }),
        map(({ status, firstName }) => ({
          status,
          firstName,
        })),
      );
  }

  autologin(hash: string) {
    this.twoFactorChallengeId = null;
    return this.autoLoginGQL
      .watch({
        ...{
          fetchPolicy: 'no-cache',
        },
        variables: {
          token: hash,
        },
      })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        take(1),
        map((result) => result.data['autoLogin']),
        RxjsOperators.filterTruthy(),
        tap(({ token }) => this.jwt.setToken(token)),
      );
  }

  private normalizeChallengeId(value: unknown): string | null {
    const challengeId = String(value || '').trim();

    return challengeId || null;
  }
}
